
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";

const NuvemshopCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleCallback = async () => {
      console.log('🔄 [CALLBACK] Processando callback da Nuvemshop');
      
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const success = searchParams.get('success');
      const store = searchParams.get('store');
      const storeName = searchParams.get('store_name');

      console.log('🔍 [CALLBACK] Parâmetros recebidos:', {
        hasCode: !!code,
        hasState: !!state,
        error,
        success,
        store,
        storeName
      });

      // Se é callback de sucesso direto (já processado pela Edge Function)
      if (success === 'true') {
        console.log('✅ [CALLBACK] Sucesso detectado - integracao já processada');
        
        // Notificar janela pai sobre sucesso
        if (window.opener) {
          window.opener.postMessage({
            type: 'nuvemshop-integration-success',
            data: { store, storeName }
          }, window.location.origin);
          
          // Fechar popup após delay
          setTimeout(() => window.close(), 1500);
        } else {
          // Se não for popup, usar localStorage e redirecionar
          localStorage.setItem('nuvemshop-integration-success', 'true');
          
          toast({
            title: "Integração realizada com sucesso!",
            description: `Sua loja ${storeName || store} foi conectada à plataforma.`,
          });
          
          setTimeout(() => navigate('/integrations'), 2000);
        }
        return;
      }

      // Se há erro nos parâmetros
      if (error) {
        console.error('❌ [CALLBACK] Erro OAuth:', error);
        
        // Notificar janela pai sobre erro
        if (window.opener) {
          window.opener.postMessage({
            type: 'nuvemshop-integration-error',
            error: error
          }, window.location.origin);
        } else {
          localStorage.setItem('nuvemshop-integration-error', error);
        }
        
        // Se for popup, fechar após delay
        if (window.opener) {
          setTimeout(() => window.close(), 2000);
        } else {
          navigate('/integrations');
        }
        return;
      }

      // Se não há code ou state, é erro
      if (!code || !state) {
        console.error('❌ [CALLBACK] Parâmetros ausentes - code ou state');
        
        const errorMsg = 'Parâmetros de autorização ausentes';
        
        if (window.opener) {
          window.opener.postMessage({
            type: 'nuvemshop-integration-error',
            error: errorMsg
          }, window.location.origin);
          setTimeout(() => window.close(), 2000);
        } else {
          toast({
            title: "Erro na integração",
            description: errorMsg,
            variant: "destructive",
          });
          navigate('/integrations');
        }
        return;
      }

      // Se tem code e state, processar via Edge Function
      console.log('🔄 [CALLBACK] Processando code de autorização');
      
      try {
        const response = await fetch(`/functions/v1/nuvemshop-integration/callback?code=${code}&state=${state}`, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        console.log('✅ [CALLBACK] Processamento concluído com sucesso');

        // A Edge Function já deve ter redirecionado, mas como fallback:
        if (window.opener) {
          window.opener.postMessage({
            type: 'nuvemshop-integration-success',
            data: { processed: true }
          }, window.location.origin);
          setTimeout(() => window.close(), 1000);
        } else {
          localStorage.setItem('nuvemshop-integration-success', 'true');
          toast({
            title: "Integração realizada com sucesso!",
            description: "Sua loja Nuvemshop foi conectada à plataforma.",
          });
          navigate('/integrations');
        }

      } catch (error: any) {
        console.error('❌ [CALLBACK] Erro ao processar:', error);
        
        // Notificar janela pai sobre erro
        if (window.opener) {
          window.opener.postMessage({
            type: 'nuvemshop-integration-error',
            error: error.message
          }, window.location.origin);
          setTimeout(() => window.close(), 2000);
        } else {
          toast({
            title: "Erro na integração",
            description: error.message || "Não foi possível conectar com a Nuvemshop",
            variant: "destructive",
          });
          navigate('/integrations');
        }
      }
    };

    handleCallback();
  }, [searchParams, navigate, toast]);

  const success = searchParams.get('success');
  const error = searchParams.get('error');

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4 max-w-md mx-auto p-6">
        {success === 'true' ? (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-semibold text-green-700">Integração Realizada!</h2>
            <p className="text-muted-foreground">
              Sua loja Nuvemshop foi conectada com sucesso. Esta janela será fechada automaticamente.
            </p>
          </>
        ) : error ? (
          <>
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto" />
            <h2 className="text-2xl font-semibold text-red-700">Erro na Integração</h2>
            <p className="text-muted-foreground">
              {error === 'access_denied' 
                ? 'Você cancelou a autorização. Tente novamente se desejar conectar sua loja.'
                : `Erro: ${error}`
              }
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-16 h-16 animate-spin mx-auto text-primary" />
            <h2 className="text-2xl font-semibold">Processando integração...</h2>
            <p className="text-muted-foreground">
              Aguarde enquanto conectamos sua loja Nuvemshop. Isso pode levar alguns segundos.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default NuvemshopCallback;
